// frontend/src/components/wizard/StepMapping/SearchableSelect.jsx
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X } from 'lucide-react';
import { useLanguageStore } from '../../../store/useLanguageStore';

/**
 * SearchableSelect — dropdown con búsqueda integrada.
 *
 * Props:
 *   value        — valor seleccionado actual (string)
 *   onChange      — callback (value: string) => void
 *   groups       — Array<{ label: string|null, options: Array<{ value, label }> }>
 *   displayLabel — texto visible en el trigger (string)
 *   triggerClass — clases CSS para el botón trigger
 *   disabled     — deshabilitar el componente
 */
export const SearchableSelect = ({
  value, onChange, groups, displayLabel, triggerClass, disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const { t } = useLanguageStore();

  // ── Click outside → cerrar (incluye portal) ─────────────────────
  const dropdownRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inContainer && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // ── Escape → cerrar ──────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // ── Autofocus search al abrir ────────────────────────────────────
  useEffect(() => {
    if (isOpen && searchRef.current) {
      // Delay para que el DOM renderice el dropdown
      requestAnimationFrame(() => searchRef.current?.focus());
    }
    if (!isOpen) setSearch('');
  }, [isOpen]);

  // ── Calcular posición fija del dropdown ──────────────────────────
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [isOpen]);

  // ── Filtrar opciones por búsqueda ────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups
      .map(g => ({
        ...g,
        options: g.options.filter(o => o.label.toLowerCase().includes(q)),
      }))
      .filter(g => g.options.length > 0);
  }, [groups, search]);

  const totalFiltered = filtered.reduce((sum, g) => sum + g.options.length, 0);

  // ── Seleccionar opción ───────────────────────────────────────────
  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* ── Trigger ─────────────────────────────────────────────── */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        className={`w-full text-left flex items-center justify-between gap-1 pl-2 pr-1.5 py-1.5 text-[11px] md:text-[12px] font-mono rounded-md border outline-none transition-all truncate ${triggerClass} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className="truncate flex-1">{displayLabel}</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* ── Dropdown (portal → document.body) ───────────────────── */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border border-slate-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
          style={{
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
            maxHeight: '260px',
            zIndex: 9999,
          }}
        >
          {/* Search input */}
          <div className="p-1.5 border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('wizard.mapping.search_source') || 'Buscar columna...'}
                className="w-full pl-7 pr-6 py-1.5 text-[11px] md:text-[12px] font-mono border border-slate-200 rounded-md bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 transition"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div ref={listRef} className="overflow-y-auto flex-1 overscroll-contain">
            {totalFiltered === 0 ? (
              <div className="px-3 py-4 text-center text-[11px] text-slate-400">
                {t('wizard.mapping.no_results') || 'Sin resultados'}
              </div>
            ) : (
              filtered.map((group, gi) => (
                <div key={gi}>
                  {group.label && (
                    <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100 sticky top-0">
                      {group.label}
                    </div>
                  )}
                  {group.options.map(opt => {
                    const isSelected = opt.value === value;
                    // Para N:M, mostrar solo la parte después del punto
                    const hasPrefix = opt.label.includes('.');
                    const colPart = hasPrefix ? opt.label.split('.').slice(1).join('.') : opt.label;

                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleSelect(opt.value)}
                        className={`w-full text-left px-2.5 py-1.5 text-[11px] md:text-[12px] font-mono transition-colors flex items-center gap-1.5 ${isSelected
                          ? 'bg-indigo-50 text-indigo-700 font-bold'
                          : 'text-slate-700 hover:bg-indigo-50/50'
                          }`}
                      >
                        {opt.icon && <span className="text-[10px] shrink-0">{opt.icon}</span>}
                        <span className="truncate">{colPart}</span>
                        {hasPrefix && !group.label && (
                          <span className="text-[9px] text-slate-400 ml-auto shrink-0">{opt.label.split('.')[0]}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
